import { LayersTwo01, MagicWand02 } from '@untitledui/icons'
import { useId, useState } from 'react'
import { usePress } from 'react-aria'
import { useLocation, useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useHeaderContext } from '@/lib/provider/HeaderContext'
import { Drawer, DrawerContent } from '@components/designSystem/Drawer'
import { Popover, PopoverContent } from '@components/designSystem/Popover'
import { type DimType, enabledLayerPerDimType } from '@components/storeMap/provider/Provider'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import ScanModeToggle from '@components/storeMap/toolbar/ScanModeToggle'
import ShelfSearch from '@components/storeMap/toolbar/ShelfSearch'
import ToolbarButton from '@components/toolbar/ToolbarButton'
import ZoomButtons from '@components/toolbar/ZoomButtons'
import { useCurrentStoreId } from '@hooks/useCurrentStore.ts'
import { useIsWideLayout } from '@hooks/useMediaQuery.ts'
import { isTodoPage, useSelectedDay } from '@hooks/useSelectedDay'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useLayout } from '@state/layout/queries'
import { useArePanoramasAvailable } from '@state/panorama.ts'
import { routesFactory } from '@utils/routes.ts'
import { useStockLevelMapper } from '@utils/stockLevelMapper.ts'

import Dimension2D from './assets/2dTile.webp'
import Map360 from './assets/360Tile.webp'
import Dimension3D from './assets/3dTile.webp'
import Category from './assets/CategoryTile.webp'
import DataAge from './assets/DataAgeTile.svg?no-inline'
import OOS from './assets/OOSTile.webp'
import Unknowns from './assets/UnknownTile.webp'

export function FloatingToolbar() {
  const { t } = useTypedTranslation()
  const isWideLayout = useIsWideLayout()
  const { isFullPage } = useHeaderContext()
  const { isHidingUnknown } = useStockLevelMapper()
  const navigate = useNavigate()
  const storeId = useCurrentStoreId()
  const arePanoramasAvailable = useArePanoramasAvailable()
  const { pathname } = useLocation()
  const { day, dayScan } = useSelectedDay()

  const [isLayerSelectionOpen, setLayerSelectionOpen] = useState(false)

  const showPanoramaTile = arePanoramasAvailable && isWideLayout

  const {
    showToolbar,
    setActiveLayer, unsetActiveLayer, isLayerActive,
    dimType, setDimType,
    selectedShelfId, setSelectedShelfId,
    mapOrigin, setMapOrigin,
    zoomIn, zoomOut,
    zoomAtLimit,
    isTransformed,
  } = useStoreMapContext()

  // shares the query cache with the map itself, so this does not cause an extra request
  const { data: layout } = useLayout({ mapOrigin })
  const deployDbAvailable = layout?.deployDbAvailable ?? false

  if (!showToolbar) return null

  const className = twJoin(
    'absolute z-50 flex flex-col justify-end gap-2',
    !isFullPage && 'top-0 right-3',
    isFullPage && isWideLayout && 'top-25 right-8',
    isFullPage && !isWideLayout && 'top-1 right-3'
  )

  const activatedFeatureForMap = enabledLayerPerDimType[dimType]

  function selectDimension(dimType: DimType) {
    setDimType(dimType)
    setLayerSelectionOpen(false)
  }

  const layerSelectionContent = (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-4'>
        <p className='font-medium'>{t('storeMap.layers.headingMapType')}</p>
        <div className='flex gap-2'>
          <TileButton
            label={t('storeMap.layers.mapType.2d')}
            imgSrc={Dimension2D}
            isActive={dimType === '2D'}
            onPress={() => selectDimension('2D')}
          />
          <TileButton
            label={t('storeMap.layers.mapType.3d')}
            imgSrc={Dimension3D}
            isActive={dimType === '3D'}
            onPress={() => selectDimension('3D')}
          />
          {
            showPanoramaTile && (
              <TileButton
                label={t('storeMap.layers.mapType.360')}
                imgSrc={Map360}
                onPress={() => navigate(routesFactory.panorama(storeId))}
              />
            )
          }
        </div>
      </div>
      <div className='h-px bg-border-secondary'></div>
      <div className='flex flex-col gap-4'>
        <p className='font-medium'>{t('storeMap.layers.headingExtraInfo')}</p>
        <div className='flex flex-wrap gap-2'>
          <TileButton
            label={t('storeMap.layers.extra.oos')}
            imgSrc={OOS}
            isActive={isLayerActive('HIGHLIGHT_OUT_OF_SHELF')}
            isDisabled={!activatedFeatureForMap.HIGHLIGHT_OUT_OF_SHELF}
            onPress={() => isLayerActive('HIGHLIGHT_OUT_OF_SHELF') ? unsetActiveLayer('HIGHLIGHT_OUT_OF_SHELF') : setActiveLayer('HIGHLIGHT_OUT_OF_SHELF')}
          />
          {!isHidingUnknown && (
            <TileButton
              label={t('storeMap.layers.extra.unknown')}
              imgSrc={Unknowns}
              isActive={isLayerActive('HEATMAP_UNKNOWNS')}
              isDisabled={!activatedFeatureForMap.HEATMAP_UNKNOWNS}
              onPress={() => isLayerActive('HEATMAP_UNKNOWNS') ? unsetActiveLayer('HEATMAP_UNKNOWNS') : setActiveLayer('HEATMAP_UNKNOWNS')}
            />
          )}
          <TileButton
            label={t('storeMap.layers.extra.categories')}
            imgSrc={Category}
            isActive={isLayerActive('CATEGORY_GROUPING')}
            isDisabled={!activatedFeatureForMap.CATEGORY_GROUPING}
            onPress={() => isLayerActive('CATEGORY_GROUPING') ? unsetActiveLayer('CATEGORY_GROUPING') : setActiveLayer('CATEGORY_GROUPING')}
          />
          {day && !dayScan && (
            <TileButton
              label={t('storeMap.layers.extra.dataAge')}
              imgSrc={DataAge}
              isActive={isLayerActive('DATA_AGE')}
              isDisabled={!activatedFeatureForMap.DATA_AGE}
              onPress={() => isLayerActive('DATA_AGE') ? unsetActiveLayer('DATA_AGE') : setActiveLayer('DATA_AGE')}
            />
          )}
        </div>
        {activatedFeatureForMap.CATEGORY_GROUPING_DEBUG && (
          <TileButton
            label={t('storeMap.layers.extra.categoriesDebug')}
            imgSrc={Category}
            isActive={isLayerActive('CATEGORY_GROUPING_DEBUG')}
            onPress={() => isLayerActive('CATEGORY_GROUPING_DEBUG') ? unsetActiveLayer('CATEGORY_GROUPING_DEBUG') : setActiveLayer('CATEGORY_GROUPING_DEBUG')}
          />
        )}
      </div>
    </div>
  )

  return (
    <div className={className}>
      <ShelfSearch
        selectedShelfId={selectedShelfId}
        onShelfIdSelect={setSelectedShelfId}
      />

      {isWideLayout ? (
        <Popover isOpen={isLayerSelectionOpen} onOpenChange={setLayerSelectionOpen}>
          <ToolbarButton
            label={t('storeMap.layers.tooltip')}
            isActive={isLayerSelectionOpen}
            shouldDisableTooltipOnActive={true}
            data-testid='layer-selection-btn'>
            <LayersTwo01 />
          </ToolbarButton>
          <PopoverContent placement='end top' dialogClassName='p-6' offset={8}>
            {layerSelectionContent}
          </PopoverContent>
        </Popover>
      ) : (
        <Drawer isOpen={isLayerSelectionOpen} onOpenChange={setLayerSelectionOpen}>
          <ToolbarButton
            label={t('storeMap.layers.tooltip')}
            isActive={isLayerSelectionOpen}
            shouldDisableTooltipOnActive={true}
            data-testid='layer-selection-btn'
            onClick={() => setLayerSelectionOpen(true)}
          >
            <LayersTwo01 />
          </ToolbarButton>
          <DrawerContent>
            {layerSelectionContent}
          </DrawerContent>
        </Drawer>
      )}

      {isWideLayout && (
        <ZoomButtons
          zoomIn={zoomIn}
          zoomInDisabled={zoomAtLimit === 'MAX'}
          zoomOut={zoomOut}
          zoomOutDisabled={zoomAtLimit === 'MIN' || (dimType === '2D' && !isTransformed)}
        />
      )}
      {day && !isTodoPage(pathname) && <ScanModeToggle />}
      <ToolbarButton
        label={dayScan
          ? t('storeMap.changeMapOrigin.SINGLE_SCAN')
          : deployDbAvailable
            ? t(`storeMap.changeMapOrigin.${mapOrigin}`)
            : t('storeMap.changeMapOrigin.UNAVAILABLE')}
        isActive={!dayScan && deployDbAvailable && mapOrigin === 'DEPLOY_DB'}
        isDisabled={!!dayScan || !deployDbAvailable}
        onClick={() => setMapOrigin(mapOrigin === 'DEPLOY_DB' ? 'SCAN' : 'DEPLOY_DB')}
      >
        <MagicWand02/>
      </ToolbarButton>
    </div>
  )
}

interface LayerButtonProps {
  label: string
  imgSrc: string
  onPress: () => void
  isActive?: boolean
  isDisabled?: boolean
}

function TileButton({ label, imgSrc, onPress, isActive, isDisabled }: LayerButtonProps) {

  const descriptionId = useId()
  const { pressProps } = usePress({ onPress, isDisabled })

  return (
    // a fixed width, so tiles line up in columns whatever their labels
    <div className={twJoin('flex w-22 flex-col items-center gap-2 text-center', isDisabled ? 'opacity-50' : '')}>
      <button
        type='button'
        aria-pressed={isActive ?? false}
        aria-labelledby={descriptionId}
        className={twJoin(
          'size-18 rounded-2xl border-3 border-transparent bg-contain bg-center bg-no-repeat ring-brand transition-all',
          'hover:ring-4 focus-visible:ring-4',
          isActive && !isDisabled && 'ring-4',
          isDisabled ? 'pointer-events-none' : 'cursor-pointer'

        )}
        style={{ backgroundImage: `url(${imgSrc})` }}
        disabled={isDisabled}
        {...pressProps}
      />
      <p id={descriptionId} className='font-medium'>{label}</p>
    </div>
  )
}
